import { useEffect, useReducer } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import type { Context, HotelImagesState } from '../types/hotel-images';
import type { UBFFGateway } from '@shared/types/frontend/ubff-gateway';
import type {
  HotelGallery,
  HotelImages,
  HotelReviewImage,
} from '@shared/types/hotel-content';
import type { Translate } from '@shared/types/i18n/translation-service';

import { NavigationItems } from '@hotels-hotel-detail/utils/gallery-navigation';
import { geImagesGalleryProps } from '@hotels-hotel-detail/utils/hotel-images';
import { HASH_HOTELS_GALLERY } from '@shared/constants/hash-routes';

export type Props = {
  hotelId: string;
  locale: string;
  market: string;
  currency: string;
  uBFFGateway: UBFFGateway;
  translate: Translate;
} & Partial<HotelImages>;

const reducer = (
  state: HotelImagesState,
  update: Partial<HotelImagesState>,
): HotelImagesState => ({
  ...state,
  ...update,
});

const useHotelImagesStore = ({
  currency,
  gallery,
  hotelId,
  imageCount,
  locale,
  market,
  reviewImages,
  translate,
  uBFFGateway,
}: Props): Context => {
  const [state, setState] = useReducer(reducer, {
    isLoading: true,
    galleryIndex: 0,
    showNavigation: true,
    images: geImagesGalleryProps({ gallery, reviewImages, translate }),
    imageCount: imageCount ?? 0,
    selectedType: NavigationItems.hotel,
  });
  const navigate = useNavigate();
  const location = useLocation();

  const getAllImages = async () => {
    const response = await uBFFGateway.getHotelImages({
      hotelId,
      locale,
      market,
      currency,
    });
    const {
      gallery: completeGallery,
      imageCount: completeImageCount,
      reviewImages: completeReviewImages,
    } = response?.hotel || {};
    if (completeImageCount && completeGallery?.images?.length) {
      setState({
        isLoading: false,
        imageCount: completeImageCount,
        images: geImagesGalleryProps({
          gallery: completeGallery as HotelGallery,
          reviewImages: completeReviewImages as HotelReviewImage[],
          translate,
        }),
      });
    } else {
      setState({ isLoading: false });
    }
  };

  useEffect(() => {
    getAllImages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    state,
    actions: {
      openGallery: ({ index, showNavigation, type }) => {
        setState({ galleryIndex: index, selectedType: type, showNavigation });
        navigate(
          { ...location, hash: HASH_HOTELS_GALLERY },
          {
            preventScrollReset: true,
          },
        );
      },
      setSelectedNavType: (selectedNav) => {
        setState({ selectedType: selectedNav });
      },
    },
  };
};

export default useHotelImagesStore;
